<template>
  <CompactStatusButton
    class="!tw-pl-4 !tw-pr-3"
    :class="containerClass"
    :tooltip="tooltip"
    :label="actionLabel"
    :disabled="reviewLoading"
    :data-heap="`${trackingPrefix}-review-status-${
      hasUpToDateReviewByUser ? 'unmark-as-reviewed' : 'mark-as-reviewed'
    }`"
    data-testid="reconciliation-review-status"
    @click="toggleReviewed(!hasUpToDateReviewByUser)"
  >
    <template #default>
      <component
        :is="counterIcon"
        v-if="hasAnyUpToDateReview"
        class="tw-block tw-w-4 tw-h-4 workflow-status-icon"
      />
      <i
        v-else-if="reviewed"
        class="
          fa fa-exclamation-circle
          tw-text-yellow-600
          tw-text-xs
          tw-flex
          tw-items-center
          tw-justify-center
          tw-w-4
          tw-h-4
        "
        data-testid="reconciliation-review-status-previously-reviewed-icon"
      />
      <img
        v-else
        :src="$imagesRequire('workflows/crossed_check.svg')"
        class="tw-block tw-w-4 tw-h-4"
      />
    </template>
    <template #hover>
      <i
        v-if="hasUpToDateReviewByUser"
        class="
          fa fa-close
          tw-text-sf-gray-dark
          tw-text-sm
          tw-leading-4
          tw-flex
          tw-items-center
          tw-justify-center
          tw-w-4
          tw-h-4
        "
      />
      <WorkflowIconReviewed
        v-else
        class="tw-block tw-w-4 tw-h-4 workflow-status-icon"
      />
    </template>
  </CompactStatusButton>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import useI18n from '@app/shared/vue/composables/use-I18n'
import {
  formatCompactTooltip,
  formatReviewTooltip,
} from '@app/application/workflows/utils/format-preparation-and-review'
import { getReviewCounterIcon } from '@app/shared/assets/icons/review-counter-icons'
import WorkflowIconReviewed from '@app/shared/assets/icons/svg/icon-reviewed.svg?component'
import CompactStatusButton from './compact-status-button.vue'
import type { TemplateReview } from '@app/application/workflows/types'

const I18n = useI18n()

interface Props {
  hasAnyUpToDateReview: boolean
  hasUpToDateReviewByUser: boolean
  reviewLoading?: boolean
  reviewed?: boolean
  reviewsToShow: Array<TemplateReview>
  trackingPrefix: string
}

const props = withDefaults(defineProps<Props>(), {
  reviewed: false,
  reviewLoading: false,
})

const emit = defineEmits<{ 'toggle-reviewed': [boolean] }>()

const containerClass = computed(() => {
  if (props.reviewLoading) return '!tw-bg-white'
  if (props.hasUpToDateReviewByUser) return '!tw-bg-emerald-50'
  if (props.hasAnyUpToDateReview) return '!tw-bg-neutral-100'
  if (props.reviewed) return '!tw-bg-yellow-50'

  return '!tw-bg-white'
})

// The button shows the current-state icon by default and swaps to the
// destination-state icon on :hover via CSS. We render both icons (toggled
// with group-hover classes) so the tooltip directive is not re-bound on
// hover, which would otherwise prevent the tooltip from appearing.
const counterIcon = computed(() =>
  getReviewCounterIcon(
    props.hasUpToDateReviewByUser,
    props.reviewsToShow.length
  )
)

const titleKey = computed(() =>
  props.hasUpToDateReviewByUser
    ? 'reconciliation_review.unmark'
    : 'reconciliation_review.mark_as_reviewed'
)

const actionLabel = computed(() => I18n.t(titleKey.value))

const tooltip = computed(() => {
  const detail = props.reviewed
    ? formatReviewTooltip(props.reviewsToShow, props.hasAnyUpToDateReview)
    : ''

  return formatCompactTooltip(actionLabel.value, detail)
})

function toggleReviewed(reviewed: boolean) {
  emit('toggle-reviewed', reviewed)
}
</script>
